import React, { useEffect, useState, useRef } from "react";
import { Box, Flex, Badge } from "theme-ui";
import { themevals } from "theme/themevals";
import { getCalendarEvents, CalendarEvent } from "api/Dashboard";
import ChevronIcon from "components/svg/icons/ChevronIcon";
import BasicButton from "components/forms/BasicButton";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import ProductsIcon from "components/svg/icons/ProductsIcon";

const COMPONENT_HEIGHT = 500;
const DAYS_OF_WEEK = ["MON", "TUES", "WED", "THURS", "FRI", "SAT", "SUN"];

interface CalendarViewProps {
  role?: 'admin' | 'manager';
}

// Event category color mapping
const EVENT_CATEGORY_COLORS: { [key: string]: string } = {
  installation: "#7FA843", // Green
  delivery: "#B33E3E", // Red  
  consultation: "#D4A636", // Yellow/Gold
  service: "#4A90E2", // Blue
  default: "#818181", // Gray
};

const CalendarView: React.FC<CalendarViewProps> = ({ role = 'admin' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Format month for API call (YYYY-MM)
  const formatMonthForAPI = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  // Fetch events for current month
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const monthParam = formatMonthForAPI(currentDate);
      const data = await getCalendarEvents(monthParam, role);
      setEvents(data?.events || []);
      setLoading(false);
    };
    fetchEvents();
  }, [currentDate, role]);

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Get month name and year
  const getMonthName = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  // Generate calendar days array
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month (adjusted for Monday start)
    const firstDay = new Date(year, month, 1);
    let firstDayOfWeek = firstDay.getDay() - 1; // Convert to Monday = 0
    if (firstDayOfWeek === -1) firstDayOfWeek = 6; // Sunday becomes 6

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Days from previous month
    const prevMonthDays = firstDayOfWeek;
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    // Build calendar days array
    const days: Array<{ date: number; month: "prev" | "current" | "next"; fullDate: Date }> = [];

    // Add previous month days
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      days.push({
        date: prevMonthLastDay - i,
        month: "prev",
        fullDate: new Date(year, month - 1, prevMonthLastDay - i),
      });
    }

    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: i,
        month: "current",
        fullDate: new Date(year, month, i),
      });
    }

    // Add next month days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: i,
        month: "next",
        fullDate: new Date(year, month + 1, i),
      });
    }

    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    return events.filter((event) => {
      const eventDate = new Date(event.date).toISOString().split("T")[0];
      return eventDate === dateString;
    });
  };

  // Get event category color
  const getEventColor = (event: CalendarEvent) => {
    // Use statusColor if available, otherwise derive from serviceType
    if (event.statusColor) return event.statusColor;
    
    const serviceType = event.serviceType?.toLowerCase() || '';
    if (serviceType.includes('install')) return EVENT_CATEGORY_COLORS.installation;
    if (serviceType.includes('deliver')) return EVENT_CATEGORY_COLORS.delivery;
    if (serviceType.includes('consult')) return EVENT_CATEGORY_COLORS.consultation;
    if (serviceType.includes('service')) return EVENT_CATEGORY_COLORS.service;
    
    return EVENT_CATEGORY_COLORS.default;
  };

  // Map status color (hex) to badge variant
  const getStatusVariant = (statusColor: string) => {
    // Common status color to variant mappings
    const colorMap: { [key: string]: string } = {
      '#7FA843': 'success',      // Green
      '#4CAF50': 'success',      // Green alt
      '#B33E3E': 'danger',       // Red
      '#F44336': 'danger',       // Red alt
      '#D4A636': 'caution',      // Yellow/Gold
      '#FFC107': 'caution',      // Yellow alt
      '#4A90E2': 'info',         // Blue
      '#2196F3': 'info',         // Blue alt
      '#104E9A': 'info',         // Blue dark
      '#818181': 'neutral',      // Gray
      '#9E9E9E': 'neutral',      // Gray alt
    };
    
    return colorMap[statusColor] || 'info';
  };

  // Handle event click and position popover
  const handleEventClick = (event: CalendarEvent, clickEvent: React.MouseEvent) => {
    const target = clickEvent.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    
    // Position to the right of the clicked element, or left if not enough space
    const spaceOnRight = window.innerWidth - rect.right;
    const popoverWidth = 320;
    
    let left = rect.right + 10; // 10px gap
    if (spaceOnRight < popoverWidth + 20) {
      left = rect.left - popoverWidth - 10;
    }
    
    setPopoverPosition({
      top: rect.top + window.scrollY,
      left: left + window.scrollX,
    });
    setSelectedEvent(event);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setSelectedEvent(null);
        setPopoverPosition(null);
      }
    };

    if (selectedEvent) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [selectedEvent]);

  const calendarDays = generateCalendarDays();

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 100%",
        minHeight: `${COMPONENT_HEIGHT}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "16px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          {getMonthName(currentDate)}
        </Box>
        <Flex sx={{ gap: "8px" }}>
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handlePrevMonth}
            styles={{
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ChevronIcon width="12px" height="12px" direction="left" margin="0" />
          </BasicButton>
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handleNextMonth}
            styles={{
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ transform: "rotate(180deg)", display: "flex", alignItems: "center" }}>
              <ChevronIcon width="12px" height="12px" direction="left" margin="0" />
            </Box>
          </BasicButton>
        </Flex>
      </Flex>

      {/* Calendar Grid */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "24px",
          overflowY: "auto",
        }}
      >
        {/* Days of Week Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            borderBottom: "none",
          }}
        >
          {DAYS_OF_WEEK.map((day, index) => {
            const isLastColumn = (index + 1) % 7 === 0;
            return (
              <Box
                key={day}
                sx={{
                  borderRight: isLastColumn ? "none" : "1px solid",
                  borderBottom: "1px solid",
                  borderColor: themevals.colors.inputOutline,
                  textAlign: "center",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: themevals.colors.base_500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  padding: "12px 8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {day}
              </Box>
            );
          })}
        </Box>

        {/* Calendar Days Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gridAutoRows: "1fr",
            gap: "0",
            flex: 1,
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            borderTop: "none",
          }}
        >
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDate(day.fullDate);
            const isOtherMonth = day.month !== "current";
            const isLastColumn = (index + 1) % 7 === 0;
            const isLastRow = index >= calendarDays.length - 7;

            return (
              <Box
                key={index}
                sx={{
                  borderRight: isLastColumn ? "none" : "1px solid",
                  borderBottom: isLastRow ? "none" : "1px solid",
                  borderColor: themevals.colors.inputOutline,
                  borderRadius: "0",
                  padding: "8px",
                  minHeight: "120px",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                {/* Day number */}
                <Box
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "16px",
                    fontWeight: 700,
                    color: isOtherMonth ? themevals.colors.mdgrey : "base_900",
                    marginBottom: "8px",
                    textAlign: "center",
                  }}
                >
                  {day.date}
                </Box>

                {/* Events */}
                <Flex sx={{ flexDirection: "column", gap: "4px", flex: 1, overflow: "hidden" }}>
                  {dayEvents.slice(0, 3).map((event) => (
                    <Box
                      key={event.id}
                      onClick={(e) => handleEventClick(event, e)}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "4px",
                        padding: "6px 8px",
                        fontSize: "11px",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        color: "black",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: themevals.colors.base_200,
                        borderLeft: "4px solid",
                        borderLeftColor: getEventColor(event),
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0px 2px 6px rgba(0,0,0,0.08)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {event.projectName || event.clientName}
                    </Box>
                  ))}
                </Flex>

                {/* Event indicator dots for remaining events */}
                {dayEvents.length > 3 && (
                  <Flex
                    sx={{
                      gap: "4px",
                      justifyContent: "flex-start",
                      marginTop: "auto",
                      paddingTop: "6px",
                    }}
                  >
                    {dayEvents.slice(3).map((event, idx) => (
                      <Box
                        key={idx}
                        onClick={(e) => handleEventClick(event, e)}
                        sx={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: getEventColor(event),
                          cursor: "pointer",
                          transition: "transform 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.3)",
                          },
                        }}
                      />
                    ))}
                  </Flex>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Loading State */}
        {loading && (
          <Flex
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                color: "base_500",
              }}
            >
              Loading...
            </Box>
          </Flex>
        )}
      </Box>

      {/* Event Detail Popover */}
      {selectedEvent && popoverPosition && createPortal(
        <Box
          ref={popoverRef}
          sx={{
            position: "absolute",
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            zIndex: 1000,
            width: "320px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: themevals.colors.base_200,
            boxShadow: "0px 84px 23px 0px rgba(0, 0, 0, 0.00), 0px 53px 21px 0px rgba(0, 0, 0, 0.01), 0px 30px 18px 0px rgba(0, 0, 0, 0.03), 0px 13px 13px 0px rgba(0, 0, 0, 0.04), 0px 3px 7px 0px rgba(0, 0, 0, 0.05)",
            borderLeft: "6px solid",
            borderLeftColor: getEventColor(selectedEvent),
          }}
        >
          {/* Header Section with Padding */}
          <Box sx={{ padding: "20px", paddingBottom: 0 }}>
            {/* Row 1: Project Name and Status */}
            <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px", gap: "16px" }}>
              <Link
                to={`/projects/${selectedEvent.projectId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: themevals.colors.base_900,
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                  flex: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = themevals.colors.primary;
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = themevals.colors.base_900;
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                {selectedEvent.projectName}
              </Link>
              
              {/* Status Badge */}
              <Badge
                as="span"
                variant={selectedEvent.statusColor}
              >
                {selectedEvent.status}
              </Badge>
            </Flex>

            {/* Row 2: GMP Number and Yardage stacked */}
            {selectedEvent.sequence && (
              <Box sx={{ marginBottom: "20px" }}>
                <Link
                  to={`/projects/${selectedEvent.projectId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    color: themevals.colors.dkgrey,
                    textDecoration: "underline",
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                    fontWeight: 600,
                    display: "block",
                    marginBottom: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = themevals.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = themevals.colors.dkgrey;
                  }}
                >
                  GMP-{String(selectedEvent.sequence).padStart(5, "0")}
                </Link>
                
                {selectedEvent.yardage && (
                  <Flex sx={{ alignItems: "center", gap: "8px" }}>
                    <ProductsIcon width="14px" height="14px" color={themevals.colors.dkgrey} />
                    <Box
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "black",
                      }}
                    >
                      {selectedEvent.yardage} yds
                    </Box>
                  </Flex>
                )}
              </Box>
            )}
          </Box>

          {/* Divider - Full Width */}
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: themevals.colors.inputOutline,
            }}
          />

          {/* Details Section with Padding */}
          <Box sx={{ padding: "20px" }}>

            {/* Client */}
            <Box sx={{ marginBottom: "16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "base_500",
                  marginBottom: "4px",
                }}
              >
                Client
              </Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {selectedEvent.clientName}
              </Box>
            </Box>

            {/* Address */}
            <Box sx={{ marginBottom: "16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "base_500",
                  marginBottom: "4px",
                }}
              >
                Address
              </Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {selectedEvent.address}
              </Box>
            </Box>

            {/* Provider/Depot */}
            <Box sx={{ marginBottom: "16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "base_500",
                  marginBottom: "4px",
                }}
              >
                Provider/Depot
              </Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {selectedEvent.provider}
              </Box>
            </Box>

            {/* Service Type */}
            <Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "base_500",
                  marginBottom: "4px",
                }}
              >
                Service Type
              </Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {selectedEvent.serviceType}
              </Box>
            </Box>
          </Box>
        </Box>,
        document.body
      )}
    </Box>
  );
};

export default CalendarView;
